import { APPEARANCE_ACTION_TYPES } from "../../constants/enrolledAppearanceConstants";

const initialState = {
  appearances: [],
  editAppearance: {},
  createdAppearance: {},
  loading: false,
  error: null,
  deleteLoading: false,
  deleteSuccess: false,
  deleteError: null,
  createLoading: false,
  createError: null,
  createSuccess: false,
  updating: false,
  updateError: null,
  updateSuccess: false,
  submitting: false,
  submitError: null,
  submitSuccess: false,
  enrolling: false,
  enrollError: null,
  enrollSuccess: false,
  associatedStreams: [],
  associatedStreamsLoading: false,
  associatedStreamsError: null,
  imageUploadStatus: {},
  imageDeleteStatus: {},
  uploadCount: 0,
  imageCache: {}, // Store blob URLs by appearanceId -> { imageId: blobUrl }
  imageLoadingStates: {}, // Track loading state per appearance
  retryingImageIds: {}, // Track imageIds currently being retried
  // Advanced Config State
  advancedConfigAppearances: [], // Selected appearances for advanced config
  advancedConfigThresholds: {
    appearance_capture_threshold: "",
    appearance_matching_threshold: ""
  },
  advancedConfigRadioSelection: "option1", // Radio button selection (option1 or option2)

};

const syncImageCacheStatuses = (imageCache, appearances) => {
  const updatedCache = { ...imageCache };
  appearances.forEach((appearance) => {
    const appId = appearance.id;
    if (updatedCache[appId]) {
      const currentImageIds = new Set((appearance.images || []).map((img) => img.imageId));
      const updatedAppCache = {};
      Object.entries(updatedCache[appId]).forEach(([imageId, entry]) => {
        if (!currentImageIds.has(imageId)) return; // prune deleted images
        const latestImage = (appearance.images || []).find((img) => img.imageId === imageId);
        updatedAppCache[imageId] = latestImage ? { ...entry, status: latestImage.status } : entry;
      });
      updatedCache[appId] = updatedAppCache;
    }
  });
  return updatedCache;
};

const enrolledAppearanceReducer = (state = initialState, action) => {
  switch (action.type) {
    case APPEARANCE_ACTION_TYPES.FETCH_APPEARANCES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        imageLoadingStates: {}
      };

    case APPEARANCE_ACTION_TYPES.FETCH_APPEARANCES_SUCCESS: {
      const { response, appearanceId } = action.payload;

      return {
        ...state,
        loading: false,
        appearances: !appearanceId ? response : state.appearances,
        editAppearance: appearanceId ? response?.[0] : {},
        error: null,
        imageCache: syncImageCacheStatuses(state.imageCache, response),
        uploadCount: response.length === 1 ? (response?.[0]?.images?.length || 0) : state.uploadCount,
        retryingImageIds: {},
      };
    }

    case APPEARANCE_ACTION_TYPES.FETCH_APPEARANCES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        retryingImageIds: {},
      };

    case APPEARANCE_ACTION_TYPES.DELETE_APPEARANCE_REQUEST:
      return {
        ...state,
        deleteLoading: true,
        deleteSuccess: false,
        deleteError: null,
      };

    case APPEARANCE_ACTION_TYPES.DELETE_APPEARANCE_SUCCESS:
      return {
        ...state,
        deleteLoading: false,
        deleteSuccess: true,
        appearances: state.appearances.filter(
          (appearance) => appearance.id !== action.payload
        ),
        deleteError: null,
      };

    case APPEARANCE_ACTION_TYPES.DELETE_APPEARANCE_FAILURE:
      return {
        ...state,
        deleteLoading: false,
        deleteError: action.payload
      };

    case APPEARANCE_ACTION_TYPES.UPDATE_APPEARANCE_REQUEST:
      return {
        ...state,
        updating: true,
        updateError: null,
        updateSuccess: false,
      };

    case APPEARANCE_ACTION_TYPES.UPDATE_APPEARANCE_SUCCESS:
      return {
        ...state,
        updating: false,
        appearances: state.appearances.map((appearance) =>
          appearance.id === action.payload.id ? action.payload : appearance
        ),
        updateError: null,
        updateSuccess: true,
      };

    case APPEARANCE_ACTION_TYPES.UPDATE_APPEARANCE_FAILURE:
      return {
        ...state,
        updating: false,
        updateError: action.payload,
        updateSuccess: false,
      };

    case APPEARANCE_ACTION_TYPES.SUBMIT_APPEARANCE_REQUEST:
      return {
        ...state,
        submitting: true,
        submitError: null,
        submitSuccess: false,
      };

    case APPEARANCE_ACTION_TYPES.SUBMIT_APPEARANCE_SUCCESS: {
      const { id, status, lastUpdated } = action.payload || {};
      return {
        ...state,
        submitting: false,
        appearances: Array.isArray(state.appearances)
          ? state.appearances.map((a) =>
              a.id === id ? { ...a, status, lastUpdated } : a
            )
          : state.appearances,
        submitError: null,
        submitSuccess: true,
      };
    }

    case APPEARANCE_ACTION_TYPES.SUBMIT_APPEARANCE_FAILURE:
      return {
        ...state,
        submitting: false,
        submitError: action.payload,
        submitSuccess: false,
      };

    case APPEARANCE_ACTION_TYPES.SET_SELECTED_APPEARANCE:
      return {
        ...state,
        editAppearance: action.payload
      };

    case APPEARANCE_ACTION_TYPES.CLEAR_APPEARANCE_ERROR:
      return {
        ...state,
        error: null,
        deleteError: null,
        deleteSuccess: false
      };

    case APPEARANCE_ACTION_TYPES.CREATE_APPEARANCE_REQUEST:
      return {
        ...state,
        createLoading: true,
        createError: null,
        createSuccess: false
      };

    case APPEARANCE_ACTION_TYPES.CREATE_APPEARANCE_SUCCESS:
      return {
        ...state,
        createdAppearance: action.payload.appearanceObj,
        appearances: [...state.appearances, action.payload.appearanceObj],
        createLoading: false,
        createError: null,
        createSuccess: true
      };

    case APPEARANCE_ACTION_TYPES.CREATE_APPEARANCE_FAILURE:
      return {
        ...state,
        createLoading: false,
        createError: action.payload
      };

    case APPEARANCE_ACTION_TYPES.RESET_CREATE_SUCCESS:
      return {
        ...state,
        createSuccess: false
      };

    case APPEARANCE_ACTION_TYPES.RESET_CREATE_STATES:
      return {
        ...state,
        createSuccess: false,
        createLoading: false,
        createError: null,
        updateSuccess: false,
        deleteSuccess: false,
        deleteError: null,
        uploadCount: 0,
        imageUploadStatus: {},
        imageDeleteStatus: {}
      };

    case APPEARANCE_ACTION_TYPES.FETCH_ASSOCIATED_STREAMS_REQUEST:
      return {
        ...state,
        associatedStreamsLoading: true,
        associatedStreamsError: null,
      };

    case APPEARANCE_ACTION_TYPES.FETCH_ASSOCIATED_STREAMS_SUCCESS:
      return {
        ...state,
        associatedStreamsLoading: false,
        associatedStreams: action.payload,
        associatedStreamsError: null,
      };

    case APPEARANCE_ACTION_TYPES.FETCH_ASSOCIATED_STREAMS_FAILURE:
      return {
        ...state,
        associatedStreamsLoading: false,
        associatedStreamsError: action.payload,
      };
      
    case APPEARANCE_ACTION_TYPES.UPLOAD_APPEARANCE_IMAGE_REQUEST: {
      const { fileId } = action.payload;
      return {
        ...state,
        imageUploadStatus: {
          ...state.imageUploadStatus,
          [fileId]: { status: "uploading", imageId: "" }
        }
      };
    }

    case APPEARANCE_ACTION_TYPES.UPLOAD_APPEARANCE_IMAGE_SUCCESS: {
      const { fileId, data, imageUrl, imageId: explicitImageId } = action.payload;
      const images = data?.images;
      const existingImgs = state.imageCache[data?.id] ?? {};
      const newImage = explicitImageId
        ? images.find((img) => img.imageId === explicitImageId)
        : images.filter(({ imageId }) => !existingImgs[imageId])[0];
      const imageId = newImage?.imageId ?? "";
      const imageStatus = newImage?.status ?? null;

      const updatedImageCache = syncImageCacheStatuses(state.imageCache, [data]);
      const appId = data?.id;
      if (imageId && imageUrl) {
        if (!updatedImageCache[appId]) updatedImageCache[appId] = {};
        updatedImageCache[appId] = { ...updatedImageCache[appId], [imageId]: { blobUrl: imageUrl, status: imageStatus, imageName: newImage?.imageName || null } };
      }

      return {
        ...state,
        editAppearance: data,
        appearances: state.appearances.map((a) =>
          a.id === appId ? { ...a, images: data.images } : a
        ),
        imageCache: updatedImageCache,
        imageUploadStatus: {
          ...state.imageUploadStatus,
          [fileId]: { status: "success", imageId, imageStatus, imageName: newImage?.imageName ?? null }
        },
        uploadCount: state.uploadCount + 1
      };
    }

    case APPEARANCE_ACTION_TYPES.UPLOAD_APPEARANCE_IMAGE_FAILURE: {
      const { fileId, error } = action.payload;
      return {
        ...state,
        imageUploadStatus: {
          ...state.imageUploadStatus,
          [fileId]: { status: "error", imageId: "", error }
        }
      };
    }

    case APPEARANCE_ACTION_TYPES.DELETE_APPEARANCE_IMAGE_REQUEST: {
      const { imageId } = action.payload;
      return {
        ...state,
        imageDeleteStatus: {
          ...state.imageDeleteStatus,
          [imageId]: { status: "deleting" }
        }
      };
    }

    case APPEARANCE_ACTION_TYPES.DELETE_APPEARANCE_IMAGE_SUCCESS: {
      const { imageId, appearanceId } = action.payload;
      const updatedDeleteStatus = { ...state.imageDeleteStatus };

      // Remove the corresponding entry from imageUploadStatus using mediaId
      delete updatedDeleteStatus[imageId]

      // Remove image from imageCache
      let updatedImageCache = { ...state.imageCache };
      if (updatedImageCache[appearanceId]) {
        const updatedAppearanceCache = { ...updatedImageCache[appearanceId] };
        delete updatedAppearanceCache[imageId];
        updatedImageCache[appearanceId] = updatedAppearanceCache;
      }
      // Always remove image from appearances and editAppearance arrays to prevent re-download attempts
      const filterImages = (list) =>
        list.map((appearance) => {
          if (appearance.id === appearanceId && appearance.images) {
            return {
              ...appearance,
              images: appearance.images.filter((img) => img.imageId !== imageId)
            };
          }
          return appearance;
        });

      const updatedAppearances = filterImages(state.appearances);
      const [updatedEditAppearance] = filterImages([state.editAppearance]) || [{}];

      return {
        ...state,
        imageCache: updatedImageCache,
        appearances: updatedAppearances,
        editAppearance: updatedEditAppearance,
        imageDeleteStatus: {
          ...state.imageDeleteStatus,
          [imageId]: { status: "deleted" }
        },
        uploadCount: Math.max(0, state.uploadCount - 1)
      };
    }

    case APPEARANCE_ACTION_TYPES.DELETE_APPEARANCE_IMAGE_FAILURE: {
      const { imageId, error } = action.payload;
      return {
        ...state,
        imageDeleteStatus: {
          ...state.imageDeleteStatus,
          [imageId]: { status: "error", error }
        }
      };
    };

    case APPEARANCE_ACTION_TYPES.DOWNLOAD_APPEARANCE_IMAGES_REQUEST:
      return {
        ...state,
        imageLoadingStates: {
          ...state.imageLoadingStates,
          [action.payload.appearanceId]: {
            loading: true,
            error: false
          }
        },
      };

    case APPEARANCE_ACTION_TYPES.DOWNLOAD_APPEARANCE_IMAGES_SUCCESS: {
      const { appearanceId, images } = action.payload;
      const imageCache = { ...state.imageCache };
      
      // Create cache entry for this appearance if it doesn't exist
      if (!imageCache[appearanceId]) {
        imageCache[appearanceId] = {};
      }
      
      // Store blob URLs by image ID (use consistent key strategy)
      images.forEach(image => {
        if (!image) return; // Skip null/undefined images
        const imageKey = image.imageId || image.id;
        if (image.url && imageKey) {
          imageCache[appearanceId][imageKey] = {
            blobUrl: image.url,
            status: image.status,
            imageName: image.imageName || null
          };
        }
      });

      return {
        ...state,
        imageCache,
        imageLoadingStates: {
          ...state.imageLoadingStates,
          [appearanceId]: {
            loading: false,
            error: false
          },
        },
      };
    }

    case APPEARANCE_ACTION_TYPES.DOWNLOAD_APPEARANCE_IMAGES_FAILURE:
      return {
        ...state,
        imageLoadingStates: {
          ...state.imageLoadingStates,
          [action.payload.appearanceId]: {
            loading: false,
            error: true
          }
        },
      };

    case APPEARANCE_ACTION_TYPES.SET_ADVANCED_CONFIG_APPEARANCES:
      return {
        ...state,
        advancedConfigAppearances: action.payload,
      };

    case APPEARANCE_ACTION_TYPES.CLEAR_ADVANCED_CONFIG_APPEARANCES:
      return {
        ...state,
        advancedConfigAppearances: [],
        advancedConfigThresholds: {
          appearance_capture_threshold: "",
          appearance_matching_threshold: ""
        },
        advancedConfigRadioSelection: "option1",
      };

    case APPEARANCE_ACTION_TYPES.SET_ADVANCED_CONFIG_THRESHOLDS:
      return {
        ...state,
        advancedConfigThresholds: action.payload,
      };

    case APPEARANCE_ACTION_TYPES.SET_ADVANCED_CONFIG_RADIO:
      return {
        ...state,
        advancedConfigRadioSelection: action.payload,
      };

    case APPEARANCE_ACTION_TYPES.ENROLL_APPEARANCE_REQUEST:
      return {
        ...state,
        enrolling: true,
        enrollError: null,
        enrollSuccess: false,
      };

    case APPEARANCE_ACTION_TYPES.ENROLL_APPEARANCE_SUCCESS: {
      const { appearanceId, enrolledSiteIds } = action.payload || {};
      const updatedAppearances =
        enrolledSiteIds && appearanceId && Array.isArray(state.appearances)
          ? state.appearances.map((a) =>
              a.id === appearanceId ? { ...a, enrolledSiteIds } : a
            )
          : state.appearances;
      return {
        ...state,
        enrolling: false,
        enrollError: null,
        enrollSuccess: true,
        appearances: updatedAppearances,
      };
    }

    case APPEARANCE_ACTION_TYPES.ENROLL_APPEARANCE_FAILURE:
      return {
        ...state,
        enrolling: false,
        enrollError: action.payload,
        enrollSuccess: false,
      };

    case APPEARANCE_ACTION_TYPES.RESET_ENROLL_STATES:
      return {
        ...state,
        enrolling: false,
        enrollError: null,
        enrollSuccess: false,
      };

    case APPEARANCE_ACTION_TYPES.RETRY_FAILED_IMAGE_REQUEST:
      return {
        ...state,
        retryingImageIds: { ...state.retryingImageIds, [action.payload.imageId]: true },
      };

    case APPEARANCE_ACTION_TYPES.RETRY_FAILED_IMAGE_FAILURE:
      return {
        ...state,
        retryingImageIds: {},
      };

    default:
      return state;
  }
};

export default enrolledAppearanceReducer;
